import type { ComponentType } from 'react';
import { Check, Copy } from 'lucide-react';
import { cn } from '@reflex/ui/lib/utils';
import { truncateId } from '@reflex/ui/lib/format';
import { useCopyToClipboard } from '@reflex/ui/hooks/useCopyToClipboard';

interface CopyIdChipProps {
  /** Full identifier copied to the clipboard on click. */
  id: string;
  /** Leading icon, mirroring the row's label icon. */
  icon: ComponentType<{ className?: string }>;
  /** Human label used for the title/aria text, e.g. "Devbox". */
  label: string;
  className?: string;
}

/**
 * Compact, non-navigable chip that shows a truncated resource id and copies
 * the full id to the clipboard on click. Mirrors the shape and size of
 * `WorkflowResourceLink` so debug-identifier rows read as one uniform set of
 * chips — the muted tint (vs the link chip's status tint) signals that this
 * chip copies rather than navigates.
 */
export function CopyIdChip({ id, icon: Icon, label, className }: CopyIdChipProps) {
  const { copied, copy } = useCopyToClipboard();

  return (
    <button
      type="button"
      onClick={() => copy(id)}
      title={`${label}: ${id}`}
      aria-label={copied ? `Copied ${label} id` : `Copy ${label} id ${id}`}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md bg-muted/60 px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
        className,
      )}
    >
      <Icon className="h-3 w-3 shrink-0" />
      <span className="font-mono text-2xs">{truncateId(id)}</span>
      {copied ? (
        <Check className="h-3 w-3 shrink-0 text-status-success" aria-hidden="true" />
      ) : (
        <Copy className="h-3 w-3 shrink-0 opacity-60" aria-hidden="true" />
      )}
    </button>
  );
}
